// @ts-nocheck
import React, { useState, useCallback } from 'react';
import { Draggable } from '@hello-pangea/dnd';
import { useAppState, useDispatch } from '../store';
import * as api from '../api';
import type { Item, Column } from '../types';
import CellRenderer from './CellRenderer';
import ContextMenu, { type MenuItem } from './ContextMenu';

interface Props {
  item: Item;
  index: number;
  columns: Column[];
  groupColor: string;
  selected: boolean;
  onToggleSelect: () => void;
}

export default function ItemRow({ item, index, columns, groupColor, selected, onToggleSelect }: Props) {
  const state = useAppState();
  const dispatch = useDispatch();
  const [editingName, setEditingName] = useState(false);
  const [name, setName] = useState(item.name);
  const [ctxMenu, setCtxMenu] = useState<{ x: number; y: number } | null>(null);
  const [hovered, setHovered] = useState(false);

  const handleNameSave = useCallback(async () => {
    if (!name.trim()) { setName(item.name); setEditingName(false); return; }
    if (name.trim() !== item.name) {
      dispatch({ type: 'UPDATE_ITEM', id: item.id, data: { name: name.trim() } });
      await api.updateItem(item.id, { name: name.trim() });
    }
    setEditingName(false);
  }, [name, item.id, item.name, dispatch]);

  const handleDelete = async () => {
    await api.deleteItem(item.id);
    dispatch({ type: 'REMOVE_ITEM', id: item.id });
  };

  const handleDuplicate = async () => {
    const dup = await api.duplicateItem(item.id);
    dispatch({ type: 'ADD_ITEM', item: dup });
    const itemCells = state.cells.filter((c) => c.item_id === item.id);
    for (const cell of itemCells) {
      dispatch({ type: 'SET_CELL', item_id: dup.id, column_id: cell.column_id, value: cell.value });
    }
  };

  const getCellValue = (columnId: string): string => {
    const cell = state.cells.find((c) => c.item_id === item.id && c.column_id === columnId);
    return cell?.value || '';
  };

  const contextMenuItems: MenuItem[] = [
    { label: 'Edit name', onClick: () => setEditingName(true) },
    { label: 'Duplicate item', onClick: handleDuplicate },
    { divider: true, label: '' },
    { label: 'Delete item', danger: true, onClick: handleDelete },
  ];

  return (
    <Draggable draggableId={item.id} index={index}>
      {(provided, snapshot) => (
        <tr
          ref={provided.innerRef}
          {...provided.draggableProps}
          className={`item-row ${snapshot.isDragging ? 'dragging' : ''}`}
          style={{
            ...provided.draggableProps.style,
            background: selected ? '#e8e8ff' : undefined,
          }}
          onContextMenu={(e) => { e.preventDefault(); setCtxMenu({ x: e.clientX, y: e.clientY }); }}
          onMouseEnter={() => setHovered(true)}
          onMouseLeave={() => setHovered(false)}
        >
          <td style={{ borderLeft: `4px solid ${groupColor}`, position: 'relative' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 40 }}>
              {hovered || selected ? (
                <input
                  type="checkbox"
                  checked={selected}
                  onChange={onToggleSelect}
                  style={{ width: 16, height: 16, accentColor: '#6161ff', cursor: 'pointer' }}
                />
              ) : (
                <div className="drag-handle" {...provided.dragHandleProps} title="Drag to reorder">
                  ⠿
                </div>
              )}
            </div>
          </td>
          <td>
            <div className="item-name-cell">
              {editingName ? (
                <input
                  className="item-name-input"
                  autoFocus
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  onBlur={handleNameSave}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleNameSave();
                    if (e.key === 'Escape') { setName(item.name); setEditingName(false); }
                  }}
                />
              ) : (
                <span
                  className="item-name-input"
                  onClick={() => { setName(item.name); setEditingName(true); }}
                  style={{ cursor: 'pointer', flex: 1 }}
                >
                  {item.name}
                </span>
              )}
            </div>
          </td>
          {columns.map((col) => (
            <td key={col.id} style={{ width: col.width, minWidth: col.width }}>
              <CellRenderer column={col} itemId={item.id} value={getCellValue(col.id)} />
            </td>
          ))}
          <td>
            {/* Row action buttons - visible on hover */}
            <div style={{
              display: 'flex', alignItems: 'center', gap: 2, opacity: hovered ? 1 : 0,
              transition: 'opacity 0.15s', justifyContent: 'center',
            }}>
              <button
                onClick={(e) => { e.stopPropagation(); setCtxMenu({ x: e.clientX, y: e.clientY }); }}
                title="More actions"
                style={{
                  border: 'none', background: 'none', cursor: 'pointer', padding: '2px 4px',
                  borderRadius: 4, color: '#676879', fontSize: 16,
                }}
              >
                ⋯
              </button>
            </div>
          </td>
          {ctxMenu && (
            <ContextMenu
              x={ctxMenu.x}
              y={ctxMenu.y}
              items={contextMenuItems}
              onClose={() => setCtxMenu(null)}
            />
          )}
        </tr>
      )}
    </Draggable>
  );
}
